import { useState } from "react";
import { t } from "i18next";
import { Label } from "@/components/ui/label";
import { GoogleSignInButton } from "@/components/ui/GoogleSignInButton";
import { useAuthStore } from "@/store/auth.store";
import {
  userHasGoogleProvider,
  userHasPasswordProvider,
} from "@/services/auth";
import { toast } from "@/hooks/use-toast";
import { FirebaseError } from "firebase/app";
import { AUTH_ERRORS } from "@/common/errors";
import { CheckCircle2 } from "lucide-react";

export const LinkGoogleAccount = (): JSX.Element | null => {
  const { user, linkGoogleAccount, loading } = useAuthStore();
  const [linking, setLinking] = useState(false);

  if (!user || !userHasPasswordProvider(user)) {
    return null;
  }

  const isGoogleLinked = userHasGoogleProvider(user);

  const handleLinkGoogle = async (): Promise<void> => {
    setLinking(true);
    try {
      await linkGoogleAccount();
      toast({
        variant: "success",
        title: t("settings.account.linkGoogle.successTitle"),
        description: t("settings.account.linkGoogle.successDescription"),
      });
    } catch (error: unknown) {
      if (
        error instanceof FirebaseError &&
        (error.code === AUTH_ERRORS.POPUP_CLOSED ||
          error.code === "auth/popup-closed-by-user")
      ) {
        return;
      }
      toast({
        variant: "error",
        title: t("settings.account.linkGoogle.errorTitle"),
        description: t("settings.account.linkGoogle.errorDescription"),
      });
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-md">
        {t("settings.account.linkGoogle.label")}
      </Label>
      <p className="text-sm text-muted-foreground">
        {t("settings.account.linkGoogle.description")}
      </p>
      {isGoogleLinked ? (
        <div className="flex items-center gap-2 text-sm text-green-700">
          <CheckCircle2 aria-hidden className="h-4 w-4" />
          <span>{t("settings.account.linkGoogle.connected")}</span>
        </div>
      ) : (
        <GoogleSignInButton
          disabled={loading}
          label={t("settings.account.linkGoogle.button")}
          loading={linking}
          onClick={() => {
            void handleLinkGoogle();
          }}
        />
      )}
    </div>
  );
};
